(function () {
  'use strict';

  if (window.InfluencerGalleryGrid && window.InfluencerGalleryGrid.loaded) {
    return;
  }

  const instanceMap = new Map();

  function parseGalleryData(root) {
    const script = root.querySelector('[data-gallery-json]');
    if (!script) return { posts: [] };
    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      return { posts: [] };
    }
  }

  function getFocusable(container) {
    return Array.from(
      container.querySelectorAll(
        'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"]), input:not([disabled]), select:not([disabled]), textarea:not([disabled])'
      )
    );
  }

  function getScrollbarWidth() {
    const outer = document.createElement('div');
    outer.style.cssText = 'visibility:hidden;overflow:scroll;position:absolute;top:-9999px;width:100px';
    document.body.appendChild(outer);
    const width = outer.offsetWidth - outer.clientWidth;
    document.body.removeChild(outer);
    return width;
  }

  function parsePositions(positionStr) {
    if (!positionStr) return [];
    return positionStr
      .split(';')
      .map((pair) => pair.trim())
      .filter(Boolean)
      .map((pair) => {
        const [rawX, rawY] = pair.split(',').map((val) => val.trim());
        const x = Number(rawX);
        const y = Number(rawY);
        if (Number.isNaN(x) || Number.isNaN(y)) return null;
        return { x: Math.max(0, Math.min(100, x)), y: Math.max(0, Math.min(100, y)) };
      });
  }

  function getFallbackPositions(count) {
    if (!count) return [];
    const cols = count <= 3 ? count : 3;
    const rows = Math.ceil(count / cols);
    const positions = [];
    for (let row = 0; row < rows; row += 1) {
      for (let col = 0; col < cols; col += 1) {
        if (positions.length >= count) break;
        const x = cols === 1 ? 50 : 20 + col * (60 / (cols - 1));
        const y = rows === 1 ? 50 : 20 + row * (60 / (rows - 1));
        positions.push({ x, y });
      }
    }
    return positions;
  }

  function initInfluencerGallery(sectionEl) {
    const root = sectionEl.classList?.contains('influencer-gallery')
      ? sectionEl
      : sectionEl.querySelector?.('.influencer-gallery');

    if (!root || instanceMap.has(root)) return;

    const data = parseGalleryData(root);
    const posts = Array.isArray(data.posts) ? data.posts : [];
    if (!posts.length) return;

    const grid = root.querySelector('[data-gallery-grid]');
    const modal = root.querySelector('[data-gallery-modal]');
    const dialog = root.querySelector('.influencer-gallery__dialog');
    const modalImage = root.querySelector('[data-modal-image]');
    const hotspotsWrap = root.querySelector('[data-modal-hotspots]');
    const modalInfluencer = root.querySelector('[data-modal-influencer]');
    const modalCaption = root.querySelector('[data-modal-caption]');
    const modalProducts = root.querySelector('[data-modal-products]');
    const backdrop = root.querySelector('[data-gallery-backdrop]');
    const prevButton = root.querySelector('[data-gallery-prev]');
    const nextButton = root.querySelector('[data-gallery-next]');

    if (!grid || !modal || !modalImage || !modalProducts || !hotspotsWrap || !dialog) return;

    const showCaption = root.dataset.showCaption === 'true';
    const showInfluencer = root.dataset.showInfluencer === 'true';
    const enableHotspots = root.dataset.enableHotspots === 'true';
    const enableNav = root.dataset.enableNav === 'true';

    const state = {
      isOpen: false,
      activeIndex: 0,
      lastActive: null,
      scrollbarWidth: 0,
    };

    function setModalVisibility(isOpen) {
      modal.hidden = !isOpen;
      modal.setAttribute('aria-hidden', String(!isOpen));
      modal.classList.toggle('is-active', isOpen);

      if (isOpen) {
        state.scrollbarWidth = getScrollbarWidth();
        document.documentElement.style.setProperty('--scrollbar-width', `${state.scrollbarWidth}px`);
        document.body.classList.add('influencer-gallery-modal-open');
      } else {
        document.body.classList.remove('influencer-gallery-modal-open');
        document.documentElement.style.removeProperty('--scrollbar-width');
      }
    }

    function updateNavButtons() {
      if (!enableNav) return;
      if (prevButton) prevButton.disabled = state.activeIndex <= 0;
      if (nextButton) nextButton.disabled = state.activeIndex >= posts.length - 1;
    }

    function renderInfluencer(post) {
      if (!modalInfluencer) return;
      modalInfluencer.innerHTML = '';
      if (!showInfluencer || !post?.influencer_name) {
        modalInfluencer.style.display = 'none';
        return;
      }
      modalInfluencer.style.display = '';
      if (post.optional_link) {
        const link = document.createElement('a');
        link.href = post.optional_link;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        link.textContent = post.influencer_name;
        modalInfluencer.appendChild(link);
      } else {
        modalInfluencer.textContent = post.influencer_name;
      }
    }

    function renderCaption(post) {
      if (!modalCaption) return;
      if (!showCaption || !post?.caption) {
        modalCaption.style.display = 'none';
        modalCaption.textContent = '';
        return;
      }
      modalCaption.style.display = '';
      modalCaption.textContent = post.caption;
    }

    function clearActiveProduct() {
      modalProducts.querySelectorAll('.influencer-gallery__product.is-active').forEach((card) => {
        card.classList.remove('is-active');
      });
    }

    function highlightProduct(index) {
      const visiblePanel = modalProducts.querySelector('.influencer-gallery__modal-products-panel:not([hidden])');
      if (!visiblePanel) return;
      const card = visiblePanel.querySelector(`[data-product-index="${index}"]`);
      if (!card) return;
      clearActiveProduct();
      card.classList.add('is-active');
      card.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      window.setTimeout(() => {
        card.classList.remove('is-active');
      }, 1600);
    }

    function renderHotspots(post) {
      hotspotsWrap.innerHTML = '';
      if (!enableHotspots) {
        hotspotsWrap.style.display = 'none';
        return;
      }
      hotspotsWrap.style.display = '';
      const products = post?.products || [];
      if (!products.length) return;

      const parsedPositions = parsePositions(post.hotspot_positions);
      const fallbackPositions = getFallbackPositions(products.length);

      products.forEach((product, index) => {
        const position = parsedPositions[index] || fallbackPositions[index];
        if (!position) return;
        const hotspot = document.createElement('button');
        hotspot.type = 'button';
        hotspot.className = 'influencer-gallery__hotspot';
        hotspot.style.left = `${position.x}%`;
        hotspot.style.top = `${position.y}%`;
        hotspot.dataset.productIndex = String(index);
        hotspot.setAttribute('aria-label', product.title || `Product ${index + 1}`);
        hotspot.textContent = String(index + 1);

        if (product.title) {
          const tooltip = document.createElement('span');
          tooltip.className = 'influencer-gallery__hotspot-tooltip';
          tooltip.textContent = product.title;
          hotspot.appendChild(tooltip);
        }

        hotspotsWrap.appendChild(hotspot);
      });
    }

    function renderProducts(postIndex) {
      const panels = modalProducts.querySelectorAll('.influencer-gallery__modal-products-panel');
      panels.forEach((panel) => {
        const index = Number(panel.dataset.postIndex);
        if (Number.isNaN(index)) return;
        if (index === postIndex) {
          panel.hidden = false;
        } else {
          panel.hidden = true;
        }
      });
    }

    function renderPost(index) {
      const post = posts[index];
      if (!post) return;
      if (post.image_src) {
        modalImage.src = post.image_src;
      }
      modalImage.srcset = post.image_srcset || '';
      modalImage.alt = post.image_alt || '';

      renderInfluencer(post);
      renderCaption(post);
      renderProducts(index);
      renderHotspots(post);
      updateNavButtons();

      // Trigger Adsgun to apply discounts to modal prices (content was hidden on page load)
      if (typeof window.AdsGun !== 'undefined' && typeof window.AdsGun._publish === 'function') {
        requestAnimationFrame(() => {
          window.AdsGun._publish('influencer_gallery');
        });
      }
    }

    function openModal(index, trigger) {
      state.activeIndex = index;
      state.lastActive = trigger || document.activeElement;
      renderPost(index);
      setModalVisibility(true);
      state.isOpen = true;

      const focusable = getFocusable(modal);
      if (focusable.length) {
        focusable[0].focus();
      }
    }

    function closeModal() {
      if (!state.isOpen) return;
      state.isOpen = false;
      setModalVisibility(false);
      if (state.lastActive && state.lastActive.focus) {
        state.lastActive.focus();
      }
    }

    function onTileClick(event) {
      const tile = event.target.closest('[data-gallery-tile]');
      if (!tile) return;
      const index = Number(tile.dataset.index);
      if (Number.isNaN(index)) return;
      openModal(index, tile);
    }

    function onModalClick(event) {
      if (event.target.closest('[data-gallery-close]')) {
        closeModal();
        return;
      }
      if (event.target === backdrop || !dialog.contains(event.target)) {
        closeModal();
      }
    }

    function onKeydown(event) {
      if (!state.isOpen) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        closeModal();
        return;
      }
      if (!enableNav) return;
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        if (state.activeIndex > 0) {
          state.activeIndex -= 1;
          renderPost(state.activeIndex);
        }
      }
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        if (state.activeIndex < posts.length - 1) {
          state.activeIndex += 1;
          renderPost(state.activeIndex);
        }
      }
    }

    async function handleQuickAdd(button) {
      const variantId = button.dataset.variantId;
      if (!variantId) return;
      const originalText = button.textContent;
      button.disabled = true;
      button.classList.add('is-loading');
      button.textContent = 'Adding...';

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] }),
        });
        if (!response.ok) throw new Error('Add failed');
        button.classList.add('is-success');
        button.textContent = 'Added';
        window.setTimeout(() => {
          button.classList.remove('is-success');
          button.textContent = originalText;
          button.disabled = false;
        }, 2000);
      } catch (error) {
        button.textContent = 'Error';
        window.setTimeout(() => {
          button.textContent = originalText;
          button.disabled = false;
        }, 2000);
      } finally {
        button.classList.remove('is-loading');
      }
    }

    function onProductsClick(event) {
      const quickAddButton = event.target.closest('[data-variant-id]');
      if (!quickAddButton) return;
      event.preventDefault();
      handleQuickAdd(quickAddButton);
    }

    function onHotspotClick(event) {
      const hotspot = event.target.closest('.influencer-gallery__hotspot');
      if (!hotspot) return;
      const index = Number(hotspot.dataset.productIndex);
      if (Number.isNaN(index)) return;
      highlightProduct(index);
    }

    function onPrev() {
      if (state.activeIndex <= 0) return;
      state.activeIndex -= 1;
      renderPost(state.activeIndex);
    }

    function onNext() {
      if (state.activeIndex >= posts.length - 1) return;
      state.activeIndex += 1;
      renderPost(state.activeIndex);
    }

    grid.addEventListener('click', onTileClick);
    modal.addEventListener('click', onModalClick);
    modalProducts.addEventListener('click', onProductsClick);
    hotspotsWrap.addEventListener('click', onHotspotClick);
    document.addEventListener('keydown', onKeydown);
    if (prevButton) prevButton.addEventListener('click', onPrev);
    if (nextButton) nextButton.addEventListener('click', onNext);

    setModalVisibility(false);

    instanceMap.set(root, {
      destroy() {
        grid.removeEventListener('click', onTileClick);
        modal.removeEventListener('click', onModalClick);
        modalProducts.removeEventListener('click', onProductsClick);
        hotspotsWrap.removeEventListener('click', onHotspotClick);
        document.removeEventListener('keydown', onKeydown);
        if (prevButton) prevButton.removeEventListener('click', onPrev);
        if (nextButton) nextButton.removeEventListener('click', onNext);
        closeModal();
      },
    });
  }

  function destroyInfluencerGallery(sectionEl) {
    const root = sectionEl.classList?.contains('influencer-gallery')
      ? sectionEl
      : sectionEl.querySelector?.('.influencer-gallery');

    if (!root) return;

    const instance = instanceMap.get(root);
    if (instance) {
      instance.destroy();
      instanceMap.delete(root);
    }
  }

  function initAll() {
    document.querySelectorAll('.influencer-gallery').forEach((section) => {
      initInfluencerGallery(section);
    });
  }

  window.InfluencerGalleryGrid = {
    loaded: true,
    init: initInfluencerGallery,
    destroy: destroyInfluencerGallery,
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initInfluencerGallery(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    destroyInfluencerGallery(event.target);
  });
})();
